import { useMemo, useState } from "react";
import { Tab } from "./components/types";
import { useSearchParams } from "react-router";

export const useLocalStorageTabs = (
  initialTabs: Tab[],
  enableEditing: boolean,
  defaultPath: string,
  editableLocationKey?: string
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") ?? defaultPath;

  const [tabs, setTabs] = useState<Tab[]>(
    enableEditing
      ? loadTabsFromStorage(initialTabs, editableLocationKey ?? "tab-order")
      : initialTabs
  );

  const visibleTabs = useMemo(() => tabs.filter((tab) => !tab.hidden), [tabs]);

  const currentSelectedTabIndex = useMemo(() => {
    const index = visibleTabs.findIndex((tab) => tabParam === tab.path);
    return index === -1 ? 0 : index;
  }, [tabs, tabParam]);

  return {
    allTabs: tabs,
    visibleTabs,
    currentSelectedTabIndex,
    changeSelectedTab: (index: number) => {
      setSearchParams((searchParams) => {
        searchParams.set("tab", visibleTabs[index].path);
        return searchParams;
      });
    },
    updateTabs: (newTabs: Tab[]) => {
      // If selected tab is moved, find it and select it
      // if it is hidden, select 0
      const previousSelectedTab = visibleTabs[currentSelectedTabIndex];
      const newSelectedTab = newTabs.find(
        (tab) => tab.path === previousSelectedTab.path
      );

      if (!newSelectedTab || newSelectedTab.hidden) {
        const firstVisibleTab = newTabs.find((tab) => !tab.hidden);

        if (firstVisibleTab) {
          setSearchParams((searchParams) => {
            searchParams.set("tab", firstVisibleTab.path);
            return searchParams;
          });
        }
      }

      const cleanedTabs = newTabs.map((t) => ({
        path: t.path,
        hidden: t.hidden,
      }));

      localStorage.setItem(
        editableLocationKey + ".tabOrder",
        JSON.stringify(cleanedTabs)
      );

      setTabs(newTabs);
    },
  };
};

const loadTabsFromStorage = (tabs: Tab[], key: string): Tab[] => {
  const storedTabs = localStorage.getItem(key + ".tabOrder");

  if (!storedTabs) {
    return tabs;
  }

  try {
    const parsedTabs: Tab[] = JSON.parse(storedTabs);
    const filteredTabs = parsedTabs
      .filter((parsedTab) => tabs.some((tab) => tab.path === parsedTab.path))
      .map((parsedTab) => {
        const originalTab = tabs.find((tab) => tab.path === parsedTab.path);

        return {
          ...originalTab,
          ...parsedTab,
        };
      });

    // Check if any tabs are missing in storage and add them
    const missingTabs = tabs.filter(
      (tab) =>
        !filteredTabs.some((filteredTab) => filteredTab.path === tab.path)
    );

    return [...filteredTabs, ...missingTabs];
  } catch {
    return tabs;
  }
};
